import isEmpty from 'lodash/isEmpty'
import React, { useContext as useContextCore, useMemo, useCallback, useRef, useEffect } from 'react'
import { useChat, type UIMessage } from '@ai-sdk/react'
import { DefaultChatTransport, type FileUIPart } from 'ai'
import type { Blog, File, Order, Product, PurchaseOrder } from '@ztechapps/common.contract'
import type { AiChatPageContext } from '@ztechapps/common.contract'
import { useAccessToken } from '@ztechapps/common.hook'
import type { SupportedMimeType } from './AttachFileButton'
import type { ChatSession } from '@ztechapps/ai.common'
import { useChatSessions } from '@ztechapps/ai.common'
import { useDebounce } from 'react-use'
import type { EmptyChatSuggestion } from './EmptyChat'
import { DEFAULT_ASSISTANT_GREETING, DEFAULT_ASSISTANT_NAME } from '../constants'

export type AiChatProviderProps = {
  children: React.ReactNode
  /** Height of the chat widget in pixels */
  height?: number
  /** API endpoint for chat - defaults to '/api/ai/chat' */
  api?: string
  /** Optional URL builder for rendering product result cards (per-surface — storefront vs admin) */
  productUrl?: (product: Product) => string
  /** Optional URL builder for rendering blog result list (per-surface — storefront vs admin) */
  blogUrl?: (post: Blog) => string
  /** Optional URL builder for rendering the sales-order "Order Created" message (per-surface — admin only today). */
  orderUrl?: (order: Order) => string
  /** Optional URL builder for rendering the purchase-order "PO Created" message (per-surface — admin only today). */
  purchaseOrderUrl?: (po: PurchaseOrder) => string
  /** When set to a non-empty string, the chat auto-sends the value as the first user message. */
  autoSendOnOpen?: string
  /** When true, a fresh session is created before each auto-send. */
  newSessionOnAutoSend?: boolean
  /** Optional action node rendered inside the error alert and below product results. */
  helpAction?: React.ReactNode
  /** Optional suggestion cards rendered in the empty-chat placeholder. Defaults to the admin-scope cards when unset — storefront-like surfaces override this so the prompts match the scope of what the prompt will actually answer. */
  emptyChatSuggestions?: EmptyChatSuggestion[]
  /** Page state read at send time and carried on the chat request body so the model sees the open pivot. */
  pageContext?: AiChatPageContext
  /** Name the assistant goes by in the empty chat, message authors and the loading indicator. Defaults to 'PAPI'. */
  assistantName?: string
  /** Greeting shown under the name in the empty chat. Defaults to the Panel Punches text. */
  greeting?: string
  /** Optional action node rendered beside the credits-exhausted warning (e.g. a link to the AI usage page). */
  creditsAction?: React.ReactNode
  /** Optional node rendered in the chat header under the session dropdown. Nothing when unset. */
  contextLabel?: React.ReactNode
}

export type AiChatContextType = {
  /** All messages in the conversation */
  messages: UIMessage[]
  /** Current input value */
  input: string
  /** Set the input value */
  setInput: (input: string) => void
  /** Handle input change event */
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  /** Submit the current input */
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void
  /** Send a message programmatically with optional attachment */
  sendMessage: (content: string, attachmentUrl?: string, mimeType?: SupportedMimeType) => void
  /** Whether the AI is currently generating a response */
  isLoading: boolean
  /** Current error if any */
  error: Error | undefined
  /** Stop the current generation */
  stop: () => void
  /** Reload the last AI response */
  reload: () => void
  /** Clear all messages */
  clearMessages: () => void
  /** Height of the chat widget */
  height?: number
  /** Currently attached file URL (for PDF/image uploads) */
  attachmentUrl?: string
  /** Set the attachment URL */
  setAttachmentUrl: (url: string | undefined) => void
  /** Currently attached file MIME type */
  attachmentMimetype?: SupportedMimeType
  /** Set the attachment MIME type */
  setAttachmentMimetype: (mimeType: SupportedMimeType | undefined) => void
  /** Currently attached file object (for preview display) */
  attachedFile?: File
  /** Set the attached file (includes URL and metadata for preview) */
  setAttachedFile: (file: File | undefined) => void
  /** All stored sessions */
  sessions: ChatSession[]
  /** Currently active session */
  currentSession: ChatSession | null
  /** Create a new session */
  createNewSession: () => void
  /** Switch to a different session */
  switchSession: (_id: string) => void
  /** Delete a session by ID */
  deleteSession: (_id: string) => void
  /** Delete all sessions */
  deleteAllSessions: () => void
  /** Optional URL builder for rendering product result cards (per-surface — storefront vs admin) */
  productUrl?: (product: Product) => string
  /** Optional URL builder for rendering blog result list (per-surface — storefront vs admin) */
  blogUrl?: (post: Blog) => string
  /** Optional URL builder for rendering the sales-order "Order Created" message (per-surface — admin only today). */
  orderUrl?: (order: Order) => string
  /** Optional URL builder for rendering the purchase-order "PO Created" message (per-surface — admin only today). */
  purchaseOrderUrl?: (po: PurchaseOrder) => string
  /** When set to a non-empty string, the chat auto-sends the value as the first user message. */
  autoSendOnOpen?: string
  /** When true, a fresh session is created before each auto-send. */
  newSessionOnAutoSend?: boolean
  /** Optional action node rendered inside the error alert and below product results. */
  helpAction?: React.ReactNode
  /** Optional suggestion cards rendered in the empty-chat placeholder. */
  emptyChatSuggestions?: EmptyChatSuggestion[]
  /** Name the assistant goes by in the empty chat, message authors and the loading indicator. */
  assistantName: string
  /** Greeting shown under the name in the empty chat. */
  greeting: string
  /** Optional action node rendered beside the credits-exhausted warning. */
  creditsAction?: React.ReactNode
  /** Optional node rendered in the chat header under the session dropdown. */
  contextLabel?: React.ReactNode
}

const context = React.createContext<AiChatContextType>({} as AiChatContextType)
const CoreProvider = context.Provider

export const AiChatProvider = ({
  children,
  height,
  api = '/api/ai/chat',
  productUrl,
  blogUrl,
  orderUrl,
  purchaseOrderUrl,
  autoSendOnOpen,
  newSessionOnAutoSend,
  helpAction,
  emptyChatSuggestions,
  pageContext,
  assistantName = DEFAULT_ASSISTANT_NAME,
  greeting = DEFAULT_ASSISTANT_GREETING,
  creditsAction,
  contextLabel,
}: AiChatProviderProps) => {
  const [attachmentUrl, setAttachmentUrl] = React.useState<string | undefined>(undefined)
  const [attachmentMimetype, setAttachmentMimetype] = React.useState<SupportedMimeType | undefined>(undefined)
  const [attachedFile, setAttachedFile] = React.useState<File | undefined>(undefined)
  const accessToken = useAccessToken()

  // Session management
  const sessionManager = useChatSessions()
  const { sessions, currentSession, isInitialized } = sessionManager

  // Session-scoped attachment refs. Populated on SEND (handleSubmit / sendMessage)
  // — NOT mirrored from state — so the auto-clear of the input chip after each
  // send (which prevents the bubble from re-rendering the thumbnail on turn 2+)
  // does not also wipe the value `transport.body()` needs across turns to keep
  // `context.attachment` populated for the multi-turn PO flow.
  // Cleared only on session change, clearMessages, or when a fresh attachment
  // overwrites the prior one on the next send.
  const attachmentUrlRef = useRef<string | undefined>(undefined)
  const attachmentMimetypeRef = useRef<SupportedMimeType | undefined>(undefined)
  const attachedFileRef = useRef<File | undefined>(undefined)

  // Page context ref, updated every render so `transport.body()` reads the
  // open pivot at send time — `useChat` freezes options at first render.
  const pageContextRef = useRef<AiChatPageContext | undefined>(pageContext)
  pageContextRef.current = pageContext

  // Create transport with custom API endpoint and authentication
  // Use body function to dynamically include attachment data at request time
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api,
        headers: accessToken ? { 'login-token': accessToken } : undefined,
        body: () => ({
          ...(attachmentUrlRef.current
            ? {
                attachmentUrl: attachmentUrlRef.current,
                attachmentMimetype: attachmentMimetypeRef.current,
                attachmentFilename: attachedFileRef.current?.name,
              }
            : {}),
          ...(pageContextRef.current ? { pageContext: pageContextRef.current } : {}),
        }),
      }),
    [api, accessToken]
  )

  // Build chat options with transport.
  // Note: Cast transport to any to avoid version mismatch between ai and @ai-sdk/react packages.
  // Attachment state is intentionally NOT cleared on `onFinish` — multi-turn flows
  // (e.g. createPurchaseOrder asking the user to confirm a new vendor before creating
  // the PO) need the attachment to survive the next request so the tool's
  // `context.attachment` remains populated and the file gets linked to the PO via
  // `createPurchaseOrder_assets`. The attachment clears on session change (effect
  // below) or via the manual-remove button in `AiChat.tsx`.
  const chatOptions = useMemo(
    () => ({
      transport: transport as any,
    }),
    [transport]
  )

  const { messages, sendMessage: chatSendMessage, regenerate, stop, setMessages, status, error } = useChat(chatOptions)

  // Local input state since v6 doesn't provide it directly
  const [input, setInput] = React.useState('')

  // Derive isLoading from status
  const isLoading = status === 'submitted' || status === 'streaming'

  // Load current session's messages when session changes or initializes.
  // Also reset any in-flight attachment — a fresh session means a fresh
  // conversation, and a leftover PDF/PNG from the previous session would
  // otherwise tag along on the first message of the new one.
  useEffect(() => {
    if (isInitialized && currentSession) {
      setMessages(currentSession.messages)
      setAttachmentUrl(undefined)
      setAttachmentMimetype(undefined)
      setAttachedFile(undefined)
      attachmentUrlRef.current = undefined
      attachmentMimetypeRef.current = undefined
      attachedFileRef.current = undefined
    }
    // Keyed on the session's _id, not the whole `currentSession` object, on purpose — the object
    // gets a new reference every time a message streams in (see syncMessages below), and
    // depending on the full object would reset messages/attachment mid-conversation, breaking the
    // multi-turn attachment flow the comment above this effect describes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession?._id, isInitialized, setMessages])

  // Debounce message sync to localStorage to avoid excessive writes during streaming
  // 500ms delay ensures we batch updates during rapid message streaming from AI responses
  const [, cancel] = useDebounce(
    () => {
      if (isInitialized && !isEmpty(messages)) {
        sessionManager.syncMessages(messages)
      }
    },
    500, // Delay in ms - balances between data safety and performance
    [messages, isInitialized]
  )

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInput(e.target.value)
  }, [])

  // Custom submit that handles attachments
  const handleSubmit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault()
      // Allow submission if there's text OR an attachment
      if (input.trim() || attachmentUrl) {
        // Build FileUIPart array if attachment exists
        const files: FileUIPart[] | undefined = attachmentUrl
          ? [
              {
                type: 'file' as const,
                mediaType: attachmentMimetype ?? 'application/octet-stream',
                url: attachmentUrl,
                filename: attachedFile?.name,
              },
            ]
          : undefined

        // Capture session-scoped attachment refs for transport.body().
        // Subsequent turns reuse the captured value even after the input chip clears.
        if (attachmentUrl) {
          attachmentUrlRef.current = attachmentUrl
          attachmentMimetypeRef.current = attachmentMimetype
          attachedFileRef.current = attachedFile
        }

        chatSendMessage({
          text: input.trim() || 'Please analyze this file.',
          files,
        })
        setInput('')

        // Clear input chip after send — the file is now committed to the conversation
        // (rendered in turn 1's bubble, captured in session refs for the wire).
        // Without this, every subsequent reply would re-attach the same file to
        // its user-message bubble and to localStorage. (PO-#1164 regression.)
        if (attachmentUrl) {
          setAttachmentUrl(undefined)
          setAttachmentMimetype(undefined)
          setAttachedFile(undefined)
        }
      }
    },
    [input, chatSendMessage, attachmentUrl, attachmentMimetype, attachedFile]
  )

  // Send a message programmatically with optional attachment
  const sendMessage = useCallback(
    (content: string, attachmentUrlOverride?: string, mimetypeOverride?: SupportedMimeType) => {
      // Determine which attachment data to use
      const url = attachmentUrlOverride ?? attachmentUrl
      const mimetype = mimetypeOverride ?? attachmentMimetype

      // Build FileUIPart array if attachment exists
      const files: FileUIPart[] | undefined = url
        ? [
            {
              type: 'file' as const,
              mediaType: mimetype ?? 'application/octet-stream',
              url,
              filename: attachedFile?.name,
            },
          ]
        : undefined

      // Capture session-scoped attachment refs for transport.body().
      if (url) {
        attachmentUrlRef.current = url
        attachmentMimetypeRef.current = mimetype
        attachedFileRef.current = attachedFile
      }

      // Send user message with files
      chatSendMessage({ text: content, files })

      // Clear input chip after send — see handleSubmit for the rationale.
      if (url) {
        setAttachmentUrl(undefined)
        setAttachmentMimetype(undefined)
        setAttachedFile(undefined)
      }
    },
    [chatSendMessage, attachmentUrl, attachmentMimetype, attachedFile]
  )

  // Clear all messages
  const clearMessages = useCallback(() => {
    setMessages([])
    setAttachmentUrl(undefined)
    setAttachmentMimetype(undefined)
    setAttachedFile(undefined)
    attachmentUrlRef.current = undefined
    attachmentMimetypeRef.current = undefined
    attachedFileRef.current = undefined
  }, [setMessages])

  // Session management functions
  const createNewSession = useCallback(() => {
    sessionManager.createSession()
    // Messages will be loaded by the effect watching currentSession
  }, [sessionManager])

  const switchSession = useCallback(
    (_id: string) => {
      // Cancel any pending debounced sync
      cancel()
      // Sync current messages before switching
      if (!isEmpty(messages) && currentSession) {
        sessionManager.syncMessages(messages)
      }
      // Switch to new session
      sessionManager.setCurrentSession(_id)
      // Messages will be loaded by the effect watching currentSession
    },
    [sessionManager, messages, currentSession, cancel]
  )

  const deleteSessionHandler = useCallback(
    (_id: string) => {
      sessionManager.deleteSession(_id)
      // Messages will be loaded by the effect watching currentSession
    },
    [sessionManager]
  )

  const deleteAllSessionsHandler = useCallback(() => {
    sessionManager.deleteAllSessions()
    // Messages will be loaded by the effect watching currentSession
  }, [sessionManager])

  const value: AiChatContextType = useMemo(
    () => ({
      messages,
      input,
      setInput,
      handleInputChange,
      handleSubmit,
      sendMessage,
      isLoading,
      error,
      stop,
      reload: regenerate,
      clearMessages,
      height,
      attachmentUrl,
      setAttachmentUrl,
      attachmentMimetype,
      setAttachmentMimetype,
      attachedFile,
      setAttachedFile,
      sessions,
      currentSession,
      createNewSession,
      switchSession,
      deleteSession: deleteSessionHandler,
      deleteAllSessions: deleteAllSessionsHandler,
      productUrl,
      blogUrl,
      orderUrl,
      purchaseOrderUrl,
      autoSendOnOpen,
      newSessionOnAutoSend,
      helpAction,
      emptyChatSuggestions,
      assistantName,
      greeting,
      creditsAction,
      contextLabel,
    }),
    [
      messages,
      input,
      setInput,
      handleInputChange,
      handleSubmit,
      sendMessage,
      isLoading,
      error,
      stop,
      regenerate,
      clearMessages,
      height,
      attachmentUrl,
      attachmentMimetype,
      attachedFile,
      sessions,
      currentSession,
      createNewSession,
      switchSession,
      deleteSessionHandler,
      deleteAllSessionsHandler,
      productUrl,
      blogUrl,
      orderUrl,
      purchaseOrderUrl,
      autoSendOnOpen,
      newSessionOnAutoSend,
      helpAction,
      emptyChatSuggestions,
      assistantName,
      greeting,
      creditsAction,
      contextLabel,
    ]
  )

  return <CoreProvider value={value}>{children}</CoreProvider>
}

export const useAiChatContext = () => useContextCore(context)
